import { useLocation, useMatch, useNavigate } from 'react-router';
import { useActiveLocation } from '../../hooks/useActiveLocation';
import { useAppDispatch } from '../../store/hooks';
import { setLastLocationId } from '../../store/application/applicationSlice';
import { DEFAULT_LOCATION_VIEW, locationPath } from '../../routes/routeConstants';
import { LocationSwitcherView } from './LocationSwitcherView';

// Container: resolves the active location and applies a switch. Rendering lives
// in LocationSwitcherView.
export const LocationSelector = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { locations, activeLocationId, isLoading, loadError } = useActiveLocation();

  // On location-scoped pages the location lives in the URL; switching navigates
  // to the same sub-path under the new location. The splat preserves whatever
  // view/sub-page is open (year-overview, settings/patients, bookings/..., etc),
  // and the query string carries over so URL state (e.g. ?year on the year
  // overview) survives the switch.
  const match = useMatch('/locations/:locationId/*');
  const isLocationScopedPage = match !== null;
  const restPath = match?.params['*'];
  const { search } = useLocation();

  const handleSelect = (nextLocationId: string): void => {
    if (isLocationScopedPage) {
      navigate({
        pathname: locationPath(nextLocationId, restPath || DEFAULT_LOCATION_VIEW),
        search
      });
      return;
    }

    // Account/global page (configuration, treatment plan builder, global
    // settings): keep the user where they are and only update the hint so
    // location-aware links retarget.
    dispatch(setLastLocationId(nextLocationId));
  };

  return (
    <LocationSwitcherView
      locations={locations}
      activeLocationId={activeLocationId}
      isLoading={isLoading}
      loadError={loadError}
      onSelect={handleSelect}
    />
  );
};
